/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { getProtectionPlanTotal } from '@/lib/extend-basket-utils';
import type { ShopperBasketsV2, ShopperOrders } from '@/scapi';

type BasketArg =
    | (Pick<
          ShopperBasketsV2.schemas['Basket'] | ShopperOrders.schemas['Order'],
          'productSubTotal' | 'orderTotal' | 'shippingTotal' | 'taxTotal' | 'orderPriceAdjustments' | 'productItems'
      > & {
          c_totalPPCost?: number;
          c_taxStatus?: string;
      })
    | null
    | undefined;

export function recomposeShopperVisibleTotal(basket: BasketArg): number {
    if (!basket) return 0;

    const protectionPlanTotal = getProtectionPlanTotal(basket);
    const productSubTotalRaw = typeof basket.productSubTotal === 'number' ? basket.productSubTotal : 0;
    const merchandiseSubtotalDisplay = Math.max(0, productSubTotalRaw - protectionPlanTotal);

    const orderAdjustmentTotal = (basket.orderPriceAdjustments ?? []).reduce(
        (sum, adj) => sum + (typeof adj.price === 'number' ? adj.price : 0),
        0
    );
    const shippingTotal = typeof basket.shippingTotal === 'number' ? basket.shippingTotal : 0;
    const taxStatus = basket.c_taxStatus;
    const taxPending = taxStatus === 'pending' || typeof basket.taxTotal !== 'number' || (basket.taxTotal ?? 0) < 0;
    const taxTotal = !taxPending && typeof basket.taxTotal === 'number' && basket.taxTotal > 0 ? basket.taxTotal : 0;

    const recomposed =
        merchandiseSubtotalDisplay + orderAdjustmentTotal + shippingTotal + protectionPlanTotal + taxTotal;

    if (taxPending) return recomposed - taxTotal;

    const hasProductSubTotal = typeof basket.productSubTotal === 'number';
    const rawOrderTotal = typeof basket.orderTotal === 'number' && basket.orderTotal >= 0 ? basket.orderTotal : null;
    return hasProductSubTotal ? recomposed : (rawOrderTotal ?? recomposed);
}
